import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils";

const badgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        outline: "border-border text-foreground",
        success: "border-transparent bg-success/10 text-success",
        warning: "border-transparent bg-warning/10 text-warning",
        destructive: "border-transparent bg-destructive/10 text-destructive",
        muted: "border-transparent bg-muted text-muted-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => (
    <span ref={ref} className={cn(badgeVariants({ variant }), className)} {...props} />
  ),
);
Badge.displayName = "Badge";

export type StatusTone = "success" | "warning" | "destructive" | "muted" | "primary" | "accent";

const dotTone: Record<StatusTone, string> = {
  success: "bg-success",
  warning: "bg-warning",
  destructive: "bg-destructive",
  muted: "bg-muted-foreground",
  primary: "bg-primary",
  accent: "bg-accent",
};

export interface StatusDotProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: StatusTone;
  /** Visible text label next to the dot. Also used as accessible label. */
  label?: string;
  /** Animate a pulse ring — use for in-progress states. */
  pulse?: boolean;
  size?: "sm" | "md";
}

const StatusDot = React.forwardRef<HTMLSpanElement, StatusDotProps>(
  ({ className, tone = "success", label, pulse = false, size = "md", ...props }, ref) => (
    <span
      ref={ref}
      className={cn("inline-flex items-center gap-2 text-xs font-medium", className)}
      {...props}
    >
      <span
        aria-hidden="true"
        className={cn(
          "relative inline-block shrink-0 rounded-full",
          size === "sm" ? "h-1.5 w-1.5" : "h-2 w-2",
          dotTone[tone],
          pulse && "motion-safe:animate-pulse-ring",
        )}
      />
      {label ? <span className={tone === "muted" ? "text-muted-foreground" : undefined}>{label}</span> : null}
      {!label && <span className="sr-only">{tone}</span>}
    </span>
  ),
);
StatusDot.displayName = "StatusDot";

export { Badge, badgeVariants, StatusDot, dotTone };
